import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  NgModule,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ChatService } from '@app/core/services/chat.service';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { formatDateByTimeZone } from '@app/shared';
import { uniqDates } from '@core/helpers/uniq';
import { DestroyableComponent } from '@models/destroyable.component';

import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { IconComponent } from '@shared/icon/icon.component';
import { TimezoneComponent } from '@shared/timezone/timezone.component';
import { addDays, eachDayOfInterval, endOfWeek, startOfDay, startOfWeek, subDays } from 'date-fns';
import { AnyType, User } from 'lingo2-models';
import { DateFnsConfigurationService, DateFnsModule, FormatPipe } from 'lingo2-ngx-date-fns';
import { DeviceDetectorService } from 'ngx-device-detector';
import { takeUntil } from 'rxjs/operators';
import { ContextService, LanguageService, ScreenService } from '../../services';
import {
  DateFnsDaysOfWeek,
  IDatetimePickerDate,
  IDatetimePickerEvent,
  IDatetimePickerPeriod,
  IDatetimePickerSelection,
  IDatetimePickerWeekday,
  localeWeekFirstDayToDateFns,
  TDatetimePickerEventStatus,
} from './models';

@Component({
  selector: 'app-datetime-picker',
  templateUrl: './datetime-picker.component.html',
  styleUrls: ['./datetime-picker.component.scss'],
})
export class DatetimePickerComponent extends DestroyableComponent implements OnInit, OnChanges {
  errorNotificationService = inject(ErrorNotificationService);
  deviceService = inject(DeviceDetectorService);
  private contextService = inject(ContextService);
  private chatService = inject(ChatService);
  private dateConfig = inject(DateFnsConfigurationService);
  private screenService = inject(ScreenService);
  private languageService = inject(LanguageService);

  @Input() timePicker: boolean;
  @Input() user_id: string;
  @Input() me: User;
  @Input() min: Date;
  @Input() max: Date;
  @Input() timeSelectLimit = 1;
  @Input() noTime: boolean;
  @Output() periodChanged = new EventEmitter<IDatetimePickerPeriod>();
  @Output() selected = new EventEmitter<Partial<IDatetimePickerSelection>>();
  @Output() closeFull = new EventEmitter<boolean>();

  public events_ready = false;
  public weekdays: IDatetimePickerWeekday[] = [];
  public daysList: IDatetimePickerDate[] = [];
  public start: Date; // начало отчёта для селектора - первый день недели
  public date: Date; // выбранный день
  public time: Date[] = []; // выбранное время
  public isPortrait = true;
  public debug: boolean;
  protected weekStartsOn: DateFnsDaysOfWeek;

  private _dateFormat = new FormatPipe(this.dateConfig, null);
  private _events: IDatetimePickerEvent[];

  @Input() set events(value: IDatetimePickerEvent[]) {
    this._events = value;
    this.zonedDate();
  }

  ngOnInit() {
    this.weekdays = this.prepareWeekdays();

    this.screenService.options.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (options) => {
        this.isPortrait = options.isPortrait;
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        this.isPortrait = false;
      },
    });

    this.contextService.debug$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (debug) => {
        this.debug = debug;
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        this.debug = false;
      },
    });

    this.languageService.language$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (language) => {
        // вычислить из Intl.Locale, недоступно в Firefox
        // @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Locale/getWeekInfo
        // @see https://caniuse.com/mdn-javascript_builtins_intl_locale_getweekinfo
        this.weekStartsOn = null;
        try {
          const locale: AnyType = new Intl.Locale(language.code);
          this.weekStartsOn = localeWeekFirstDayToDateFns(locale?.weekInfo?.firstDay);
        } catch (e) {}
        if (this.weekStartsOn === null) {
          switch (language.code) {
            case 'ru':
              this.weekStartsOn = DateFnsDaysOfWeek.MONDAY;
              break;
            default:
              this.weekStartsOn = DateFnsDaysOfWeek.SUNDAY;
              break;
          }
        }
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.events) {
      if (this._events.length) {
        this.fixDate();
        this.noTime = false;
      } else {
        this.noTime = true;
      }
      if (!this.start && this.date) {
        this.start = this.startOfWeek(this.date);
      }
      this.events_ready = true;
    }

    if (changes.events || changes.me_id || changes.user_id) {
      this.prepare();
    }
  }

  private onPeriodChange() {
    this.periodChanged.emit({
      start: this.startOfWeek(this.start),
      end: this.endOfWeek(this.start),
    });
    this.events_ready = false;
  }

  private onSelected() {
    this.selected.emit({
      date: [this.date],
      time: this.time,
    });
  }

  protected prepare() {
    if (this.start && this.user_id && this.me.id) {
      this.prepareWeekList(this.start);
      this.fixDate();
    }
  }

  protected fixDate() {
    // выбрать валидную дату
    if (!this.date || !this.hasDayEventByStatus(this.date, 'free')) {
      const _d = this.nearestFreeHour();
      if (_d) {
        this.date = startOfDay(_d);
      }
    }
  }

  // список названий дней недели
  private prepareWeekdays(): IDatetimePickerWeekday[] {
    const now = new Date();
    const interval = {
      start: this.startOfWeek(now),
      end: this.endOfWeek(now),
    };
    return eachDayOfInterval(interval).map((date) => ({
      full: this.dateFormat(date, 'iiii'),
      short: this.dateFormat(date, 'iii'),
    }));
  }

  /**
   * Перейти на предыдущую неделю можно, если this.min находится в той неделе или позже
   */
  public get canGoPreviousWeek(): boolean {
    if (!this.start) {
      return false;
    }
    const _d = this.endOfWeek(subDays(this.start, 7));
    return _d.getTime() >= this.min.getTime();
  }

  public get canGoNextWeek(): boolean {
    if (!this.start) {
      return false;
    }
    const _d = addDays(this.start, 7);
    return _d.getTime() <= this.max.getTime();
  }

  // сдвинуть дату на предыдущую неделю
  public goPreviousWeek() {
    this.start = subDays(this.start, 7);
    if (this.timeSelectLimit < 2) {
      this.time = [];
    }
    this.prepare();
  }

  // сдвинуть дату на следующую неделю
  public goNextWeek() {
    this.start = addDays(this.start, 7);
    if (this.timeSelectLimit < 2) {
      this.time = [];
    }
    this.prepare();
  }

  public get currentMonthName(): string {
    if (!this.start) {
      return '';
    }

    const end = this.endOfWeek(this.start);
    const startMonthName = this.dateFormat(this.start, 'LLLL');
    const startYear = this.dateFormat(this.start, 'yyyy');
    const endMonthName = this.dateFormat(end, 'LLLL');
    const endYear = this.dateFormat(end, 'yyyy');

    if (startYear !== endYear) {
      return [`${startMonthName} ${startYear}`, `${endMonthName} ${endYear}`].join(' — ');
    } else if (startMonthName !== endMonthName) {
      return [startMonthName, endMonthName].join(' — ');
    } else {
      return startMonthName;
    }
  }

  // создает поле с днями месяца и событиями
  private prepareWeekList(start: Date) {
    const todayTime = startOfDay(new Date()).getTime();
    const minTime = startOfDay(this.min).getTime(); // Date.now();

    const interval = {
      start: this.startOfWeek(start),
      end: this.endOfWeek(start),
    };

    this.daysList = eachDayOfInterval(interval)
      .map((date) => {
        const dateTime = date.getTime();
        const is_today = dateTime === todayTime;
        const is_active = dateTime >= minTime || is_today;
        // const has_busy = this.hasDayEventByStatus(date, 'busy');
        const has_free = this.hasDayEventByStatus(date, 'free');
        return {
          label: this.dateFormat(date, 'd'),
          date: formatDateByTimeZone(date, this.me?.timezone),
          is_active,
          is_today,
          is_busy: false, // has_busy,
          is_free: is_active && has_free, //  && !has_busy,
          events: this.prepareTimesList(date),
        };
      })
      .map((daysList) => ({
        ...daysList,
        events: daysList.events.sort((a, b) => a.date.getTime() - b.date.getTime()),
      }));
  }

  private prepareTimesList(date: Date): IDatetimePickerEvent[] {
    return this._events.filter((_event) => startOfDay(date).getTime() === startOfDay(_event.date).getTime());
  }

  public convertEventToDate(events: IDatetimePickerEvent[]): IDatetimePickerDate[] {
    const todayTime = startOfDay(new Date()).getTime();
    const minTime = startOfDay(this.min).getTime();

    let _events: IDatetimePickerDate[] = events.map((event) => {
      const dateTime = event.date.getTime();
      const is_today = dateTime === todayTime;
      const is_active = dateTime >= minTime || is_today;
      const has_busy = this.hasDayEventByStatus(event.date, 'busy');
      const has_free = this.hasDayEventByStatus(event.date, 'free');
      const debugInfo = { event, is_active, is_today, has_busy, has_free };
      return {
        label: this.dateFormat(event.date, 'HH:mm'),
        note: this.debug ? JSON.stringify(debugInfo, null, 2) : null,
        date: event.date,
        is_active,
        is_today,
        is_busy: has_busy,
        is_free: is_active && has_free && !has_busy,
      };
    });

    // не показывать недоступные часы
    _events = _events.filter((event) => event.is_free || event.is_busy);

    return _events;
  }

  protected nearestFreeHour(): Date {
    const sortedEvents = this._events.sort((e1, e2) => e1.date.getTime() - e2.date.getTime());
    const hours = uniqDates(sortedEvents.map((e) => e.date));
    return hours.find((h) => this.isHourFree(h));
  }

  /**
   * Есть ли в указанные сутки событие указанного статуса
   */
  protected hasDayEventByStatus(date: Date, status: TDatetimePickerEventStatus): boolean {
    const dayTime = startOfDay(date).getTime();
    const _dayEvents = this._events.filter((e) => {
      const eDay = startOfDay(e.date);
      return eDay.getTime() === dayTime;
    });

    const hours = uniqDates(_dayEvents.map((e) => e.date));

    switch (status) {
      case 'free':
        return hours.some((hour) => this.isHourFree(hour));

      case 'busy':
        return false;
    }
  }

  /**
   * Есть ли в указанный час событие указанного статуса
   */
  protected hasHourEventByStatus(hour: Date, status: TDatetimePickerEventStatus): boolean {
    switch (status) {
      case 'free':
        return this.isHourFree(hour);

      case 'busy':
        return false;
    }
  }

  protected isHourFree(hour: Date): boolean {
    const _hourEvents = this._events.filter((e) => e.date.getTime() === hour.getTime());
    const _userEvents = _hourEvents.filter((e) => e.account_id === this.user_id);
    const _myEvents = _hourEvents.filter((e) => e.account_id === this.me.id);

    const userIsFree = _userEvents.some((e) => e.status === 'free');
    const meIsBusy = _myEvents.some((e) => e.status === 'busy');
    return userIsFree && !meIsBusy;
  }

  // выбор дня
  // public selectDate(day: IDatetimePickerDate) {
  //   if (!this.events_ready || !day.is_active || !day.is_free) {
  //     return;
  //   }
  //   if (!this.date || this.date.getTime() !== day.date.getTime()) {
  //     this.date = day.date;
  //     if (this.timeSelectLimit < 2) {
  //       this.time = [];
  //     }
  //     this.prepareTimesList(this.date);
  //     this.onSelected();
  //   }
  // }

  public selectTime(time: IDatetimePickerDate) {
    if (!this.events_ready || !time.is_active || !time.is_free) {
      return;
    }

    if (this.timeSelectLimit < 2) {
      this.time = [time.date];
    } else {
      const sameTimeIndex = this.time.findIndex((t) => t.getTime() === time.date.getTime());
      if (sameTimeIndex >= 0) {
        // удалить
        this.time.splice(sameTimeIndex, 1);
      } else {
        // добавить
        if (this.time.length < this.timeSelectLimit) {
          this.time.push(time.date);
        }
      }
    }

    this.onSelected();
  }

  // выбран день?
  public isSelectedDate(date: IDatetimePickerDate): boolean {
    return this.date && this.date.getTime() === date.date.getTime();
  }

  // выбран день?
  public isSelectedTime(time: IDatetimePickerDate): boolean {
    return !!this.time && !!this.time.find((t) => time.date.getTime() === t.getTime());
  }

  // Утилиты для работы с временем

  private dateFormat(date: Date, format: string): string {
    return this._dateFormat.transform(date, format);
  }

  private startOfWeek(date: Date): Date {
    return startOfWeek(date, { locale: this.dateConfig.locale(), weekStartsOn: this.weekStartsOn });
  }

  private endOfWeek(date: Date): Date {
    return endOfWeek(date, { locale: this.dateConfig.locale(), weekStartsOn: this.weekStartsOn });
  }

  // отладка

  public eventOwnerNameAbbr(e: any): string {
    if (e.account_id === this.me.id) {
      return 'me';
    }

    if (e.account_id === this.user_id) {
      return 'teacher';
    }

    return '???';
  }

  public dayEventsDigest(day: IDatetimePickerDate): string {
    return this._events
      .filter((e) => startOfDay(e.date).getTime() === day.date.getTime())
      .map((e) => this.dateFormat(e.date, 'HH:mm') + ' ' + this.eventOwnerNameAbbr(e) + '/' + e.status)
      .join('\n');
  }

  public hourEventsDigest(hour: IDatetimePickerDate): string {
    return this._events
      .filter((e) => e.date.getTime() === hour.date.getTime())
      .map((e) => this.eventOwnerNameAbbr(e) + '/' + e.status)
      .join('\n');
  }

  public openChat() {
    this.closeFull.emit(true);
    this.chatService.openPrivateChat(this.user_id);
  }

  public trackByFn(index, item: IDatetimePickerDate): Date {
    return item.date;
  }

  private zonedDate(): void {
    this._events.forEach((event) => (event.date = formatDateByTimeZone(event.date, this.me?.timezone)));
  }
}

@NgModule({
  declarations: [DatetimePickerComponent, DatetimePickerComponent],
  imports: [CommonModule, DateFnsModule, TimezoneComponent, IconComponent, LocaleSharedModule],
  exports: [DatetimePickerComponent],
  providers: [],
})
export class DatetimePickerModule {}
